import { Component } from 'preact';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  PANEL_BG, GOLD_BTN, BEVELED_BTN, INPUT_STYLE,
  FourCorners, OrnamentalDivider,
} from '../lib/medievalTheme';
import { UI } from '../utils/arena/uiSounds';
import { api } from '../utils/serverClient';
import { k, t } from '../localization/t';

const CATEGORIES = [
  { value: 'bug', label: k('Bug') },
  { value: 'ui', label: k('UI / Visual') },
  { value: 'gameplay', label: k('Gameplay') },
  { value: 'performance', label: k('Performance') },
  { value: 'suggestion', label: k('Suggestion') },
  { value: 'other', label: k('Other') },
];

export default class ReportIssueDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      title: '',
      description: '',
      category: 'bug',
      submitting: false,
      submitted: false,
      issueNumber: null,
      error: null,
    };
  }

  handleSubmit = async () => {
    const { title, description, category } = this.state;
    if (title.trim().length < 3 || description.trim().length < 10) return;

    this.setState({ submitting: true, error: null });
    try {
      const result = await api.post('/reports', {
        title: title.trim(),
        description: description.trim(),
        category,
        appVersion: this.props.appVersion || null,
        platform: navigator.platform || null,
      });
      this.setState({ submitting: false, submitted: true, issueNumber: result.issueNumber });
    } catch (err) {
      this.setState({ submitting: false, error: err.message || t('Failed to submit report') });
    }
  };

  render() {
    const { onClose } = this.props;
    const { title, description, category, submitting, submitted, issueNumber, error } = this.state;
    const canSubmit = title.trim().length >= 3 && description.trim().length >= 10 && !submitting;

    return (
      <div
        className="fixed inset-0 z-[220] flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
      >
        <div
          className="relative w-[26.25cqw] max-h-[80cqh] flex flex-col"
          style={{
            background: PANEL_BG,
            border: `1px solid ${GOLD} 0.25)`,
            borderRadius: 'var(--radius-xl)',
            boxShadow: '0 0 60px rgba(0,0,0,0.5)',
            isolation: 'isolate',
          }}
        >
          <FourCorners />

          <div className="px-5 pt-5 pb-3 shrink-0">
            <h2 className="text-lg font-semibold arena-heading" style={{ color: TEXT_PRIMARY }}>
              {submitted ? t('Report Submitted') : t('Report an Issue')}
            </h2>
            {!submitted && (
              <p className="text-xs mt-1" style={{ color: TEXT_MUTED }}>
                {t("Describe the problem and we'll look into it.")}
              </p>
            )}
          </div>

          <OrnamentalDivider className="px-5 shrink-0" />

          <div className="flex-1 overflow-y-auto px-5 py-3 min-h-0">
            {submitted ? (
              <div className="text-center py-6">
                <div className="text-3xl mb-3">&#x2714;</div>
                <p className="text-sm font-medium mb-1" style={{ color: TEXT_PRIMARY }}>
                  {t('Thank you for your report!')}
                </p>
                <p className="text-xs" style={{ color: TEXT_MUTED }}>
                  {issueNumber
                    ? t('Your report has been filed as issue #{n}.', { n: issueNumber })
                    : t('Your report has been submitted successfully.')}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {/* Category */}
                <div>
                  <label className="text-[0.625cqw] font-semibold mb-1 block" style={{ color: `${GOLD} 0.45)` }}>
                    {t('Category')}
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat.value}
                        type="button"
                        className="px-3 py-1.5 text-xs font-medium cursor-pointer transition-all"
                        style={{
                          ...(category === cat.value
                            ? { background: `${GOLD} 0.15)`, border: `1px solid ${ACCENT_GOLD}`, color: ACCENT_GOLD }
                            : { ...BEVELED_BTN, color: TEXT_MUTED }),
                          borderRadius: 'var(--radius-md)',
                        }}
                        onClick={() => this.setState({ category: cat.value })}
                      >
                        {t(cat.label)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="text-[0.625cqw] font-semibold mb-1 block" style={{ color: `${GOLD} 0.45)` }}>
                    {t('Title')}
                  </label>
                  <input
                    type="text"
                    maxLength={120}
                    placeholder={t('Brief summary of the issue')}
                    value={title}
                    onInput={(e) => this.setState({ title: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-lg"
                    style={{ ...INPUT_STYLE, color: TEXT_BODY, backgroundColor: '#0e0a06' }}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="text-[0.625cqw] font-semibold mb-1 block" style={{ color: `${GOLD} 0.45)` }}>
                    {t('Description')}
                  </label>
                  <textarea
                    rows={5}
                    maxLength={2000}
                    placeholder={t('What happened? What did you expect to happen? Steps to reproduce...')}
                    value={description}
                    onInput={(e) => this.setState({ description: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-lg resize-none"
                    style={{ ...INPUT_STYLE, color: TEXT_BODY, backgroundColor: '#0e0a06' }}
                  />
                  <div className="text-[0.625cqw] text-right mt-0.5" style={{ color: TEXT_MUTED }}>
                    {description.length}/2000
                  </div>
                </div>

                {error && (
                  <p className="text-xs" style={{ color: '#e89090' }}>{error}</p>
                )}
              </div>
            )}
          </div>

          {/* Footer buttons */}
          <div className="px-5 pb-5 pt-2 flex justify-end gap-2 shrink-0">
            {submitted ? (
              <button
                type="button"
                className="px-5 py-2 text-sm font-semibold cursor-pointer transition-all"
                style={{ ...GOLD_BTN, borderRadius: 'var(--radius-md)' }}
                data-sound={UI.CONFIRM}
                onClick={onClose}
              >
                {t('Done')}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium cursor-pointer transition-all"
                  style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-md)' }}
                  data-sound={UI.CANCEL}
                  onClick={onClose}
                >
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  className="px-5 py-2 text-sm font-semibold cursor-pointer transition-all"
                  style={{
                    ...GOLD_BTN,
                    borderRadius: 'var(--radius-md)',
                    opacity: canSubmit ? 1 : 0.4,
                    pointerEvents: canSubmit ? 'auto' : 'none',
                  }}
                  data-sound={UI.CONFIRM}
                  onClick={this.handleSubmit}
                >
                  {submitting ? t('Submitting...') : t('Submit Report')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}
